import { getAlternateLinks, getPageMeta, mergeMeta } from "@dotcom/remix-meta";
import { Disclaimer } from "@dotcom/ui";
import {
  SectionSpacer,
  Spacer,
  Spacings,
} from "@polestar/component-warehouse-react";
import { json, type LoaderFunctionArgs } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";

import { HandoffTiles, Hero } from "./components";
import FeaturedPartners from "./components/FeaturedPartners/FeaturedPartners";
import { type FeaturedPartnersTileProps } from "./components/FeaturedPartners/FeaturedPartnersTile";
import LogoTiles from "./components/LogoTiles/LogoTiles";
import TextBlock from "./components/TextBlock/TextBlock";
import Video from "./components/Video/Video";
import getOverview from "./data/overview/getOverview";

import { getErrorPageData } from "~/common/data";
import {
  getIsChina,
  handlePartnershipsRedirects,
} from "~/common/helpers/.server";
import { getPartnershipsUrl } from "~/common/helpers";
import { type RootLoader } from "~/root";

export const meta = mergeMeta<typeof loader, { root: RootLoader }>(
  ({ data, location }) => {
    if (!data) return [];

    const {
      publishedMarkets,
      seo: { title, description, image, twitterCard, robotsNoindex },
    } = data.overview.meta;

    const alternateLinks = getAlternateLinks(
      location.pathname,
      publishedMarkets,
      data.isChina
    );

    /**
     * Due to legal requirements we need to change "partnerships" to "collaborations"
     * in NA markets
     */
    const alternateLinksWithCollaborations = alternateLinks.map(
      (alternateLink) => {
        const publishedMarket = publishedMarkets.find(({ locale }) => {
          return alternateLink.href.includes(`/${locale}/`);
        });

        if (publishedMarket) {
          return {
            ...alternateLink,
            href: getPartnershipsUrl(alternateLink.href, publishedMarket),
          };
        }

        return alternateLink;
      }
    );
    return [
      ...getPageMeta({
        title,
        description,
        image: image.url,
        twitterCard,
        noIndex: robotsNoindex,
      }),
      ...alternateLinksWithCollaborations,
    ];
  }
);

export const loader = async ({
  context: { market, datoClient },
  request,
}: LoaderFunctionArgs) => {
  const isChina = getIsChina();

  if (!market) {
    throw json(
      await getErrorPageData(datoClient, {
        market,
        requestUrl: request.url,
      }),
      { status: 404 }
    );
  }

  await handlePartnershipsRedirects({ market, request });

  const overview = await getOverview(datoClient, { market });
  if (!overview) {
    throw json(
      await getErrorPageData(datoClient, {
        market,
        requestUrl: request.url,
      }),
      { status: 404 }
    );
  }

  return {
    overview,
    isChina,
  };
};

const PartnershipsOverviewPage = () => {
  const { overview } = useLoaderData<typeof loader>();

  return (
    <>
      <Spacer
        spacing={{
          mobile: Spacings.xLarge,
          tablet: Spacings.xxLarge,
        }}
      />

      {overview.hero && (
        <>
          <Hero title={overview.hero.title} body={overview.hero.body} />
          <SectionSpacer />
        </>
      )}

      {overview.video && (
        <>
          <Video video={overview.video} />
          <SectionSpacer />
        </>
      )}

      <TextBlock
        title={overview.textBlock.title}
        blocks={overview.textBlock.tiles}
      />

      <SectionSpacer />

      {overview.featuredPartners.tiles.length > 0 && (
        <>
          <FeaturedPartners
            title={overview.featuredPartners.title}
            tiles={
              overview.featuredPartners.tiles as Array<
                FeaturedPartnersTileProps & {
                  _datoRecordId: string;
                }
              >
            }
          />

          <SectionSpacer />
        </>
      )}

      <LogoTiles tiles={overview.logoTiles} />

      <SectionSpacer />

      <HandoffTiles tiles={overview.handoffTiles} />

      <SectionSpacer />

      {overview.disclaimer && (
        <>
          <Disclaimer text={overview.disclaimer} />

          <SectionSpacer />
        </>
      )}
    </>
  );
};

export default PartnershipsOverviewPage;
